import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { api, type Settings as S } from "../lib/api.ts";
import { keys, useSettings } from "../lib/queries.ts";
import { toast } from "../lib/store.ts";

export function Settings() {
  const q = useSettings();
  const qc = useQueryClient();
  const [s, setS] = useState<S | null>(null);
  useEffect(() => { if (q.data && !s) setS(q.data); }, [q.data, s]);
  if (!s) return <div className="skeleton h-8 w-60" aria-busy />;

  const save = async (patch: Partial<S>) => {
    const next = { ...s, ...patch };
    setS(next);
    try {
      await api("/settings", { method: "PUT", body: patch });
      void qc.invalidateQueries({ queryKey: keys.settings });
      toast("Settings saved");
    } catch (e) {
      toast(`Couldn't save: ${(e as Error).message}`, { tone: "error" });
    }
  };
  const pct = (bps: number) => String(bps / 100);
  const num = (v: string) => (v.trim() === "" || !Number.isFinite(Number(v)) ? null : Number(v));

  return (
    <div className="grid gap-10 max-w-[40rem]">
      <div>
        <h1 className="heading text-[26px]">Settings</h1>
        <p className="text-[14px] mt-1"><Link to="/feedback" className="underline underline-offset-2">Tell Claude</Link> what's wrong or what to change.</p>
      </div>

      <section className="grid gap-4">
        <h2 className="heading text-[18px]">Real cost</h2>
        <p className="muted text-[14px] -mt-2">Used for the "all in" price on each lot.</p>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Sales tax, %" value={pct(s.taxBps)} onCommit={(v) => { const n = num(v); if (n != null) void save({ taxBps: Math.round(n * 100) }); }} />
          <Field label="Buyer's premium when unknown, %" value={pct(s.defaultPremiumBps)} onCommit={(v) => { const n = num(v); if (n != null) void save({ defaultPremiumBps: Math.round(n * 100) }); }} />
          <Field label="Transport, $ per mile (vehicles)" value={String(s.transportCentsPerMile / 100)} onCommit={(v) => { const n = num(v); if (n != null) void save({ transportCentsPerMile: Math.round(n * 100) }); }} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Home latitude" value={s.home ? String(s.home.lat) : ""} onCommit={(v) => { const lat = num(v); void save({ home: lat == null ? null : { lat, lng: s.home?.lng ?? 0 } }); }} />
          <Field label="Home longitude" value={s.home ? String(s.home.lng) : ""} onCommit={(v) => { const lng = num(v); if (lng != null && s.home) void save({ home: { lat: s.home.lat, lng } }); }} />
        </div>
        <button className="btn justify-self-start" onClick={() => navigator.geolocation?.getCurrentPosition((p) => void save({ home: { lat: +p.coords.latitude.toFixed(3), lng: +p.coords.longitude.toFixed(3) } }), () => toast("Location not available", { tone: "error" }))}>
          Use my current location
        </button>
      </section>

      <section className="grid gap-3">
        <h2 className="heading text-[18px]">Notifications</h2>
        <p className="muted text-[14px] -mt-1">Sent to your ntfy topic if the server has one set (NTFY_URL), and always listed in Hammer. Prices are checked once a day at noon Eastern, so price alerts follow that check.</p>
        {([
          ["closingSoon", "An hour and ten minutes before a lot you're bidding on closes"],
          ["overMax", "When the bid passes your max"],
          ["stillCheap", "When a watched lot is still under your price an hour before close"],
          ["fetchFailing", "When Hammer can't check a lot you're bidding on"],
        ] as const).map(([k, label]) => (
          <label key={k} className="flex gap-3 items-start">
            <input type="checkbox" className="mt-1" checked={s.notify[k]} onChange={(e) => void save({ notify: { ...s.notify, [k]: e.target.checked } })} />
            <span>{label}</span>
          </label>
        ))}
        <div className="flex items-center gap-3 mt-2 flex-wrap">
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={!!s.quietHours} onChange={(e) => void save({ quietHours: e.target.checked ? { start: 23, end: 7 } : null })} />
            Quiet hours
          </label>
          {s.quietHours && (
            <span className="flex items-center gap-2 text-[14px]">
              from <HourSelect value={s.quietHours.start} onChange={(start) => void save({ quietHours: { ...s.quietHours!, start } })} />
              to <HourSelect value={s.quietHours.end} onChange={(end) => void save({ quietHours: { ...s.quietHours!, end } })} />
              <span className="muted">(closing alerts still come through)</span>
            </span>
          )}
        </div>
      </section>

      <MacHelper />

      <LegacyImport />

      <section className="grid gap-3">
        <h2 className="heading text-[18px]">Your data</h2>
        <div className="flex gap-2 flex-wrap">
          <a className="btn" href="/api/export?format=csv">Download CSV</a>
          <a className="btn" href="/api/export">Download JSON</a>
          <button className="btn btn-quiet" onClick={async () => { await api("/logout", { method: "POST" }); location.reload(); }}>Sign out</button>
        </div>
      </section>
    </div>
  );
}

function Field({ label, value, onCommit }: { label: string; value: string; onCommit: (v: string) => void }) {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  return (
    <label className="grid">
      <span className="label">{label}</span>
      <input className="field num" inputMode="decimal" value={v} onChange={(e) => setV(e.target.value)} onBlur={() => v !== value && onCommit(v)} onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()} />
    </label>
  );
}

function HourSelect({ value, onChange }: { value: number; onChange: (h: number) => void }) {
  return (
    <select className="field !w-auto !min-h-[34px] !py-0" value={value} onChange={(e) => onChange(Number(e.target.value))}>
      {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{new Date(2000, 0, 1, h).toLocaleTimeString([], { hour: "numeric" })}</option>)}
    </select>
  );
}

function LegacyImport() {
  const qc = useQueryClient();
  const [url, setUrl] = useState("https://tracker.zakiu.com");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  return (
    <section className="grid gap-3">
      <h2 className="heading text-[18px]">Import from your old tracker</h2>
      <p className="muted text-[14px] -mt-1">Copies every lot, with its status, notes and results. Lots already here are skipped, so it's safe to run again. Anything that looks wrong goes to the inbox.</p>
      <form
        className="grid gap-3"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          setResult(null);
          try {
            const r = await api<{ created: number; skipped: number; flagged: number }>("/import/legacy", { method: "POST", body: { url, password } });
            setResult(`Imported ${r.created} lot${r.created === 1 ? "" : "s"}${r.flagged ? ` (${r.flagged} to check in the inbox)` : ""}. Skipped ${r.skipped} already here.`);
            setPassword("");
            await qc.invalidateQueries();
          } catch (err) {
            setResult(`Couldn't import: ${(err as Error).message}`);
          } finally {
            setBusy(false);
          }
        }}
      >
        <div className="grid grid-cols-2 gap-4">
          <label className="grid"><span className="label">Old tracker address</span><input className="field" value={url} onChange={(e) => setUrl(e.target.value)} /></label>
          <label className="grid"><span className="label">Its password</span><input className="field" type="password" autoComplete="off" value={password} onChange={(e) => setPassword(e.target.value)} /></label>
        </div>
        <div><button className="btn btn-primary" disabled={busy || !password}>{busy ? "Importing…" : "Import lots"}</button></div>
        {result && <p className="text-[14px]">{result}</p>}
      </form>
    </section>
  );
}

function MacHelper() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["helper"], queryFn: () => api<{ token: string; lastSeenAt: string | null; origin: string }>("/helper") });
  const [copied, setCopied] = useState(false);
  if (!q.data) return null;
  const { token, lastSeenAt, origin } = q.data;
  const command = `curl -fsSL ${origin}/hammer-helper.sh | bash -s install ${origin} ${token}`;
  const seen = lastSeenAt ? new Date(lastSeenAt) : null;
  return (
    <section className="grid gap-3">
      <h2 className="heading text-[18px]">Check blocked sites from your Mac</h2>
      <p className="muted text-[14px] -mt-1">
        HiBid, Proxibid, GovDeals, LiveAuctioneers, AllSurplus and BidSpotter refuse Hammer's server. A small helper on your Mac checks those lots once a day from your home connection, whenever the Mac is awake after noon Eastern. Some sites may refuse it too; Sources shows which.
      </p>
      <p className="text-[14px]">{seen ? <>Your Mac last checked in {seen.toLocaleString()}.</> : <>Not installed yet.</>}</p>
      <ol className="list-decimal pl-5 grid gap-1 text-[14px]">
        <li>Open Terminal (in Applications, then Utilities).</li>
        <li>Paste this and press Return:</li>
      </ol>
      <pre className="panel text-[12.5px] whitespace-pre-wrap break-all num">{command}</pre>
      <div className="flex flex-wrap gap-2">
        <button className="btn btn-primary" onClick={async () => { await navigator.clipboard.writeText(command); setCopied(true); setTimeout(() => setCopied(false), 2500); }}>{copied ? "Copied" : "Copy command"}</button>
        <button className="btn btn-quiet" onClick={async () => {
          await api("/helper/rotate", { method: "POST" });
          await qc.invalidateQueries({ queryKey: ["helper"] });
          toast("New token made. Run the new command on your Mac; the old one stops working.");
        }}>Reset token</button>
      </div>
      <p className="text-[13px] muted">To remove it: <code className="num">bash "$HOME/Library/Application Support/Hammer/hammer-helper.sh" uninstall</code></p>
    </section>
  );
}
